import _ from 'lodash'
import { eLearningScormDebugLog } from 'src/utils/apiPath'

// เก็บ log การเรียน SCORM ไว้ในหน่วยความจำ แล้วส่งขึ้น pod log ของ api-ams-learner
// เฉพาะตอนเจอ error หรือผู้เรียนกดแจ้งปัญหา ทุกส่วนห่อ try/catch ไว้เพื่อไม่ให้กระทบการเรียน

export const MAX_API_LOGS = 300
export const MAX_EVENT_LOGS = 50
export const MAX_AUTO_SEND = 3
export const AUTO_SEND_DELAY = 3000
export const SLOW_RESOURCE_MS = 5000
export const MAX_DOM_ITEMS = 60
// fetch แบบ keepalive ส่งได้ไม่เกิน 64KB และ API รับ body ไม่เกิน 100kb
export const MAX_PAYLOAD_BYTES = 60000
const MAX_TEXT = 300
const LOG_LEVEL_WARNING = 3
const LOG_LEVEL_ERROR = 4
// error ที่ content เรียกเจอได้ตามปกติ เช่น GetValue ค่าที่ยังไม่เคยตั้ง จึงไม่ส่ง log อัตโนมัติ
export const BENIGN_API_ERROR = {
  1.2: ['401'],
  2004: ['401', '403'],
}
const CANDIDATE_SELECTOR = [
  'button',
  'a[href]',
  'input[type="button"]',
  'input[type="submit"]',
  '[role="button"]',
  '[onclick]',
  '[data-acc-text]',
  '[tabindex]',
].join(',')
const FORCE_SHOW_STYLE = {
  visibility: 'visible',
  opacity: '1',
  'pointer-events': 'auto',
}

export const truncate = (value, max = MAX_TEXT) => {
  if (_.isNil(value)) return value
  const text = String(value)
  return text.length > max ? `${text.slice(0, max)}…(${text.length})` : text
}

// ตัด query string และ hash ออก เพราะอาจมี token หรือ ticket ติดมา
export const stripUrl = (url) => {
  if (!url) return url
  const path = String(url).split(/[?#]/)[0]
  try {
    const origin = window.location.origin
    return path.startsWith(origin) ? path.slice(origin.length) : path
  } catch (e) {
    return path
  }
}

const byteSize = (text) => new Blob([text]).size

export const fitPayload = (payload) => {
  const result = _.cloneDeep(payload)
  const fits = () => byteSize(JSON.stringify(result)) <= MAX_PAYLOAD_BYTES
  while (!fits() && _.size(result.data.api) > 20) {
    result.data.api = result.data.api.slice(
      Math.floor(result.data.api.length / 2),
    )
  }
  if (!fits() && _.size(result.data.dom) > 20) {
    result.data.dom = result.data.dom.slice(0, 20)
  }
  if (!fits()) {
    result.data.errors = _.takeRight(result.data.errors, 10)
    result.data.resources = _.takeRight(result.data.resources, 10)
    result.data.lifecycle = _.takeRight(result.data.lifecycle, 10)
  }
  if (!fits()) result.data.dom = []
  return result
}

export const sendScormDebugLog = async (
  payload,
  { keepalive = false } = {},
) => {
  let token = null
  try {
    token = localStorage.getItem('token')
  } catch (e) {
    token = null
  }
  if (_.isEmpty(token)) throw new Error('no token')

  // ใช้ fetch แทน axios เพื่อไม่ให้ interceptor เปิด error dialog ใส่ผู้เรียนตอนส่งอัตโนมัติ
  const response = await fetch(
    `${window.__env__.REACT_APP_API_URL}${eLearningScormDebugLog}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(fitPayload(payload)),
      credentials: 'include',
      keepalive,
    },
  )
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return response.json()
}

const pickCmi = (api, version) => {
  const data = _.get(api && api.renderCommitCMI(true), 'cmi', {})
  const suspend = String(_.get(data, 'suspend_data', '') || '')
  const base =
    version === '1.2'
      ? {
          status: _.get(data, 'core.lesson_status'),
          score: _.get(data, 'core.score'),
          location: truncate(_.get(data, 'core.lesson_location'), 100),
          entry: _.get(data, 'core.entry'),
          exit: _.get(data, 'core.exit'),
          sessionTime: _.get(data, 'core.session_time'),
        }
      : {
          completion: _.get(data, 'completion_status'),
          success: _.get(data, 'success_status'),
          score: _.get(data, 'score'),
          location: truncate(_.get(data, 'location'), 100),
          entry: _.get(data, 'entry'),
          exit: _.get(data, 'exit'),
          sessionTime: _.get(data, 'session_time'),
        }
  return {
    ...base,
    suspendDataLength: suspend.length,
    suspendDataHead: suspend.slice(0, 100),
    interactions: _.size(_.get(data, 'interactions')),
  }
}

const describeElement = (el, win, frame) => {
  const style = win.getComputedStyle(el)
  const rect = el.getBoundingClientRect()
  const label =
    el.getAttribute('aria-label') ||
    el.getAttribute('data-acc-text') ||
    el.innerText ||
    el.value ||
    ''
  return {
    frame,
    tag: el.tagName.toLowerCase(),
    id: el.id || undefined,
    model: el.getAttribute('data-model-id') || undefined,
    cls: truncate(el.getAttribute('class'), 80) || undefined,
    text: truncate(String(label).replace(/\s+/g, ' ').trim(), 50),
    display: style.display,
    visibility: style.visibility,
    opacity: style.opacity,
    pe: style.pointerEvents,
    visible: el.checkVisibility
      ? el.checkVisibility({ opacityProperty: true, visibilityProperty: true })
      : undefined,
    rect: [rect.x, rect.y, rect.width, rect.height].map(Math.round),
  }
}

const detectTool = (win) =>
  _.compact([
    win.DS && 'storyline',
    win.cp && 'captivate',
    (win.iSpring || win.ispring) && 'ispring',
    _.get(win.document.querySelector('meta[name="generator"]'), 'content'),
  ])

export const createScormDebugSession = (context) => {
  const startedAt = Date.now()
  const logs = { api: [], errors: [], resources: [], lifecycle: [] }
  const player = {}
  const frames = []
  const attachedDocuments = new WeakSet()
  let autoSentCount = 0
  let autoTimer = null

  const elapsed = () => Date.now() - startedAt
  const push = (group, item, max = MAX_EVENT_LOGS) => {
    const list = logs[group]
    list.push({ t: elapsed(), ...item })
    if (list.length > max) list.shift()
  }

  const forEachFrame = (callback) => {
    frames.forEach(({ win, path }) => {
      try {
        if (win.document) callback(win, path)
      } catch (e) {
        // frame ถูกปิดหรือเปลี่ยนไปโดเมนอื่นแล้ว
      }
    })
  }

  const collectSlowResources = () => {
    const result = []
    forEachFrame((win, path) => {
      win.performance
        .getEntriesByType('resource')
        .filter(
          (entry) =>
            entry.duration >= SLOW_RESOURCE_MS || entry.responseStatus >= 400,
        )
        .slice(-20)
        .forEach((entry) =>
          result.push({
            type: 'timing',
            frame: path,
            url: stripUrl(entry.name),
            ms: Math.round(entry.duration),
            status: entry.responseStatus,
            initiator: entry.initiatorType,
          }),
        )
    })
    return result
  }

  const collectDom = () => {
    const items = []
    const tools = []
    forEachFrame((win, path) => {
      tools.push({ frame: path, tool: detectTool(win) })
      win.document.querySelectorAll(CANDIDATE_SELECTOR).forEach((el) => {
        if (items.length < MAX_DOM_ITEMS)
          items.push(describeElement(el, win, path))
      })
    })
    return { items, tools }
  }

  const safely = (fn, fallback) => {
    try {
      return fn()
    } catch (e) {
      return fallback
    }
  }

  const buildPayload = (trigger, { withDom = false } = {}) => {
    const api = context.version === '1.2' ? window.API : window.API_1484_11
    const dom = withDom ? safely(collectDom, { items: [], tools: [] }) : null
    return {
      trigger,
      context: {
        ...context,
        startedAt: new Date(startedAt).toISOString(),
        sentAt: new Date().toISOString(),
        elapsedSec: Math.round(elapsed() / 1000),
        page: stripUrl(window.location.href),
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        dpr: window.devicePixelRatio,
        online: navigator.onLine,
        visibility: document.visibilityState,
        autoSentCount,
        tools: dom ? dom.tools : undefined,
      },
      data: {
        api: logs.api,
        errors: logs.errors,
        resources: [...logs.resources, ...safely(collectSlowResources, [])],
        lifecycle: logs.lifecycle,
        cmi: safely(() => pickCmi(api, context.version), null),
        player,
        ...(dom ? { dom: dom.items } : {}),
      },
    }
  }

  const sendAuto = ({ keepalive = false } = {}) => {
    autoSentCount += 1
    sendScormDebugLog(buildPayload('auto'), { keepalive }).catch(() => {})
  }

  const scheduleAutoSend = () => {
    if (autoTimer || autoSentCount >= MAX_AUTO_SEND) return
    // รอให้ error ที่ตามมาติดไปด้วยในการส่งครั้งเดียว
    autoTimer = setTimeout(() => {
      autoTimer = null
      sendAuto()
    }, AUTO_SEND_DELAY)
  }

  const onLogMessage = (level, message) => {
    push('api', { level, msg: truncate(message) }, MAX_API_LOGS)
    if (level === LOG_LEVEL_WARNING) console.warn(message)
    if (level < LOG_LEVEL_ERROR) return
    console.error(message)
    const code = _.get(
      String(message).match(/throwSCORMError\s*:\s*(\d+)/),
      '[1]',
    )
    const benign = _.get(BENIGN_API_ERROR, context.version, [])
    if (!benign.includes(code)) scheduleAutoSend()
  }

  const onFrameError = (event, win, path) => {
    const target = event.target
    const url = target && (target.currentSrc || target.src || target.href)
    if (target && target !== win && url) {
      push('resources', {
        type: 'error',
        frame: path,
        tag: target.tagName,
        url: stripUrl(url),
      })
    } else {
      push('errors', {
        type: 'js',
        frame: path,
        msg: truncate(event.message),
        file: stripUrl(event.filename),
        line: event.lineno,
        col: event.colno,
        stack: truncate(_.get(event, 'error.stack'), 1000),
      })
    }
    scheduleAutoSend()
  }

  // ผูก listener ใหม่ทุกครั้งที่ frame โหลดหน้าใหม่ เพราะ listener เดิมหายไปพร้อม document เก่า
  const attachFrame = (win, path = 'scorm') => {
    try {
      if (!win || win.location.href === 'about:blank') return
      const doc = win.document
      if (attachedDocuments.has(doc)) return
      attachedDocuments.add(doc)
      if (!frames.some((frame) => frame.win === win)) frames.push({ win, path })

      win.addEventListener('error', (e) => onFrameError(e, win, path), true)
      win.addEventListener('unhandledrejection', (e) => {
        const reason = _.get(e, 'reason.stack') || _.get(e, 'reason')
        push('errors', {
          type: 'promise',
          frame: path,
          msg: truncate(reason, 1000),
        })
        scheduleAutoSend()
      })
      win.addEventListener(
        'load',
        (e) => {
          if (_.get(e, 'target.tagName') === 'IFRAME')
            attachFrame(e.target.contentWindow, `${path}>iframe`)
        },
        true,
      )
      _.forEach(doc.querySelectorAll('iframe'), (child) =>
        attachFrame(child.contentWindow, `${path}>iframe`),
      )
      push('lifecycle', {
        event: 'frame-load',
        frame: path,
        url: stripUrl(win.location.href),
      })
    } catch (e) {
      push('lifecycle', {
        event: 'frame-attach-failed',
        frame: path,
        msg: truncate(e.message),
      })
    }
  }

  const setPlayer = (patch) => Object.assign(player, patch, { t: elapsed() })

  const report = () =>
    sendScormDebugLog(buildPayload('user', { withDom: true }))

  const applyWorkaround = ({ selector, display = 'block', css = '' }) => {
    let matched = 0
    forEachFrame((win) => {
      const elements = win.document.querySelectorAll(selector)
      if (!elements.length) return
      const style = win.document.createElement('style')
      const declarations = _.map(
        { display, ...FORCE_SHOW_STYLE },
        (value, key) => `${key}:${value}!important`,
      ).join(';')
      style.textContent = `${selector}{${declarations};${css}}`
      win.document.head.appendChild(style)
      // ใส่ inline style ซ้ำ เผื่อ content ซ่อนไว้ด้วย inline style ที่มี !important
      elements.forEach((el) =>
        _.forEach({ display, ...FORCE_SHOW_STYLE }, (value, key) =>
          el.style.setProperty(key, value, 'important'),
        ),
      )
      matched += elements.length
    })
    push('lifecycle', { event: 'workaround', selector, matched })
    return matched
  }

  const onVisibilityChange = () =>
    push('lifecycle', { event: 'visibility', state: document.visibilityState })
  const onOnline = () => push('lifecycle', { event: 'online' })
  const onOffline = () => push('lifecycle', { event: 'offline' })
  const flush = () => {
    if (!autoTimer) return
    clearTimeout(autoTimer)
    autoTimer = null
    sendAuto({ keepalive: true })
  }

  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
  window.addEventListener('pagehide', flush)

  const stop = () => {
    flush()
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('online', onOnline)
    window.removeEventListener('offline', onOffline)
    window.removeEventListener('pagehide', flush)
  }

  return {
    context,
    logs,
    onLogMessage,
    attachFrame,
    setPlayer,
    buildPayload,
    report,
    applyWorkaround,
    stop,
  }
}

let currentSession = null

export const getScormDebugSession = () => currentSession

export const startScormDebugSession = (context) => {
  if (currentSession) currentSession.stop()
  currentSession = createScormDebugSession(context)
  // ให้ dev เปิดดู buffer ใน console ได้ด้วย window.__scormDebug.logs
  window.__scormDebug = currentSession
  return currentSession
}

export const endScormDebugSession = (session) => {
  session.stop()
  if (currentSession !== session) return
  currentSession = null
  window.__scormDebug = undefined
}

// ตั้งค่าใน configmap (window.__env__) เช่น
// SCORM_WORKAROUND: { '<moduleUuid>': { selector: '#retry-button', display: 'block', css: '' } }
export const getScormWorkaround = (moduleUuid) => {
  const config = _.get(window.__env__, ['SCORM_WORKAROUND', moduleUuid], null)
  return _.isEmpty(_.get(config, 'selector')) ? null : config
}
